/**
 * BetaBanner — persistent slim top bar for the Beta period (BL-022 / BL-055).
 *
 * A banner (not a toast): a months-long message must persist across
 * navigation. It is PERMANENT and non-dismissible by design — every Tonnie
 * app (business-web, journey-web, citizen-mobile) shows the beta notice the
 * same way, and only the server flag (`useBetaConfig`) can hide it. There is
 * no close control. Copy arrives as already-translated strings (no i18n in
 * @tonnie/ui).
 */

import * as React from 'react';

import { cn } from '../lib/utils';

export interface BetaBannerProps {
  /** Translated banner message. */
  message: string;
  /** Translated CTA label, e.g. "Give feedback". */
  ctaLabel?: string;
  /** Invoked when the CTA is clicked (e.g. open the Feedback drawer). */
  onCtaClick?: () => void;
  className?: string;
}

export function BetaBanner({
  message,
  ctaLabel,
  onCtaClick,
  className,
}: BetaBannerProps): React.JSX.Element {
  return (
    <div
      role="status"
      data-testid="beta-banner"
      className={cn(
        'flex items-center justify-center gap-3 px-4 py-2 text-sm',
        'bg-primary text-primary-foreground',
        className
      )}
    >
      <span className="font-medium text-center">{message}</span>
      {ctaLabel && onCtaClick && (
        <button
          type="button"
          onClick={onCtaClick}
          data-testid="beta-banner-cta"
          className="shrink-0 rounded-full bg-primary-foreground text-primary-accessible-on-light hover:bg-primary-foreground/90 px-3 py-1 text-xs font-semibold transition-colors"
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
